import React, { useState, useMemo } from 'react';
import { cn, sortableHeaderProps } from '../../utils';
import type {
  WorkflowRecommendationResult,
  MatchingRecommendation,
  WorkflowStepRecommendation,
  SkipConditionRecommendation,
  OrderAnalysis,
  ThresholdTier,
  DiscoveredField,
} from '../../types';

type SortField = 'fieldName' | 'jsonPath' | 'currentValue' | 'category' | 'usefulnessScore';
type SortDirection = 'asc' | 'desc';

interface RecommendationsPanelProps {
  result: WorkflowRecommendationResult;
  onApplyMatchingRecommendation?: (recommendation: MatchingRecommendation) => void;
  onApplyApprovalLevel?: (level: WorkflowStepRecommendation) => void;
  onApplySkipCondition?: (condition: SkipConditionRecommendation) => void;
  onAddIdentifier?: (identifier: {
    name: string;
    jsonPath: string;
    operator: 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'regex' | 'greaterThan' | 'lessThan';
    value?: string;
    regexPattern?: string;
  }) => void;
  onAddRoutingRule?: (rule: {
    field: string;
    operator: string;
    value: string;
  }) => void;
  onCreateOrderAttribute?: (field: DiscoveredField) => void;
  onAddRoutingRuleToWorkflow?: (field: DiscoveredField) => void;
  className?: string;
}

export function RecommendationsPanel({
  result,
  onApplyMatchingRecommendation,
  onApplyApprovalLevel,
  onApplySkipCondition,
  onAddIdentifier,
  onAddRoutingRule,
  onCreateOrderAttribute,
  onAddRoutingRuleToWorkflow,
  className,
}: RecommendationsPanelProps) {
  const [activeTab, setActiveTab] = React.useState<'analysis' | 'matching' | 'approval' | 'skip'>('analysis');

  if (!result.success) {
    return (
      <div className={cn('bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4', className)}>
        <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
          <i className="fa-solid fa-exclamation-circle" />
          <span className="font-medium">Recommendation Generation Failed</span>
        </div>
        <p className="text-red-600 dark:text-red-400 mt-2">{result.errorMessage}</p>
      </div>
    );
  }

  const tabs = [
    { id: 'analysis' as const, label: 'Order Analysis', icon: 'fa-magnifying-glass-chart', count: undefined as number | undefined },
    { id: 'matching' as const, label: 'Matching Criteria', icon: 'fa-filter', count: result.matchingRecommendations?.length || 0 },
    { id: 'approval' as const, label: 'Steps', icon: 'fa-list-check', count: result.approvalRecommendations?.levels?.length || 0 },
    { id: 'skip' as const, label: 'Skip Conditions', icon: 'fa-forward', count: result.skipConditionRecommendations?.length || 0 },
  ];

  return (
    <div className={cn('bg-white dark:bg-secondary-800 border border-gray-200 dark:border-secondary-700 rounded-lg', className)}>
      {/* Summary Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-secondary-700 bg-gray-50 dark:bg-secondary-900">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="fa-solid fa-lightbulb text-yellow-500" />
            <span className="font-medium text-gray-900 dark:text-white">Workflow Recommendations</span>
          </div>
          {result.generatedAt && (
            <span className="text-sm text-gray-500 dark:text-secondary-400">
              Generated at {new Date(result.generatedAt).toLocaleString()}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 dark:text-secondary-400 mt-1">{result.summary}</p>
      </div>

      {/* Warnings */}
      {result.warnings && result.warnings.length > 0 && (
        <div className="px-4 py-2 bg-yellow-50 dark:bg-yellow-950 border-b border-yellow-100">
          <div className="flex items-start gap-2">
            <i className="fa-solid fa-triangle-exclamation text-yellow-500 mt-0.5" />
            <div className="flex-1">
              <span className="text-sm font-medium text-yellow-800">Observations:</span>
              <ul className="mt-1 space-y-1">
                {result.warnings.map((warning, idx) => (
                  <li key={idx} className="text-sm text-yellow-700 dark:text-yellow-400">• {warning}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 dark:border-secondary-700">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors',
              activeTab === tab.id
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 bg-blue-50 dark:bg-blue-950'
                : 'text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200 hover:bg-gray-50 dark:hover:bg-secondary-700'
            )}
          >
            <i className={cn('fa-solid', tab.icon)} />
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className={cn(
                'ml-1 px-1.5 py-0.5 text-xs rounded-full',
                activeTab === tab.id ? 'bg-blue-100 dark:bg-blue-900 text-blue-700' : 'bg-gray-100 dark:bg-secondary-700 text-gray-600 dark:text-secondary-400'
              )}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-4">
        {activeTab === 'analysis' && result.orderAnalysis && (
          <OrderAnalysisSection
            analysis={result.orderAnalysis}
            onAddIdentifier={onAddIdentifier}
            onAddRoutingRule={onAddRoutingRule}
            onCreateOrderAttribute={onCreateOrderAttribute}
            onAddRoutingRuleToWorkflow={onAddRoutingRuleToWorkflow}
          />
        )}
        {activeTab === 'matching' && result.matchingRecommendations && (
          <MatchingRecommendationsSection
            recommendations={result.matchingRecommendations}
            onApply={onApplyMatchingRecommendation}
          />
        )}
        {activeTab === 'approval' && result.approvalRecommendations && (
          <ApprovalRecommendationsSection
            recommendations={result.approvalRecommendations}
            onApply={onApplyApprovalLevel}
          />
        )}
        {activeTab === 'skip' && result.skipConditionRecommendations && (
          <SkipConditionsSection
            conditions={result.skipConditionRecommendations}
            onApply={onApplySkipCondition}
          />
        )}
      </div>
    </div>
  );
}

interface OrderAnalysisSectionProps {
  analysis: OrderAnalysis;
  onAddIdentifier?: (identifier: {
    name: string;
    jsonPath: string;
    operator: 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'regex' | 'greaterThan' | 'lessThan';
    value?: string;
    regexPattern?: string;
  }) => void;
  onAddRoutingRule?: (rule: {
    field: string;
    operator: string;
    value: string;
  }) => void;
  onCreateOrderAttribute?: (field: DiscoveredField) => void;
  onAddRoutingRuleToWorkflow?: (field: DiscoveredField) => void;
}

function OrderAnalysisSection({ analysis, onAddIdentifier, onAddRoutingRule, onCreateOrderAttribute, onAddRoutingRuleToWorkflow }: OrderAnalysisSectionProps) {
  const [sortField, setSortField] = useState<SortField>('usefulnessScore');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [filterText, setFilterText] = useState('');
  const [expandedField, setExpandedField] = useState<string | null>(null);

  const filteredAndSortedFields = useMemo(() => {
    if (!analysis.discoveredFields) return [];
    
    // Filter first
    const filterLower = filterText.toLowerCase().trim();
    const filtered = filterLower
      ? analysis.discoveredFields.filter(field =>
          (field.fieldName || '').toLowerCase().includes(filterLower) ||
          (field.jsonPath || '').toLowerCase().includes(filterLower) ||
          (field.currentValue || '').toLowerCase().includes(filterLower) ||
          (field.category || '').toLowerCase().includes(filterLower)
        )
      : analysis.discoveredFields;
    
    // Then sort
    return [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'fieldName':
          comparison = (a.fieldName || '').localeCompare(b.fieldName || '');
          break;
        case 'jsonPath':
          comparison = (a.jsonPath || '').localeCompare(b.jsonPath || '');
          break;
        case 'currentValue':
          comparison = (a.currentValue || '').localeCompare(b.currentValue || '');
          break;
        case 'category':
          comparison = (a.category || '').localeCompare(b.category || '');
          break;
        case 'usefulnessScore':
          comparison = (a.usefulnessScore ?? 0) - (b.usefulnessScore ?? 0);
          break;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [analysis.discoveredFields, sortField, sortDirection, filterText]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'usefulnessScore' ? 'desc' : 'asc');
    }
  };

  const SortableHeader = ({ field, label, className }: { field: SortField; label: string; className?: string }) => (
    <th
      className={cn(
        'pb-2 pt-3 cursor-pointer hover:text-gray-700 dark:hover:text-secondary-200 select-none transition-colors',
        className
      )}
      {...sortableHeaderProps(() => handleSort(field), sortField === field, sortDirection)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        <span className="text-xs" aria-hidden="true">
          {sortField === field ? (
            sortDirection === 'asc' ? (
              <i className="fa-solid fa-sort-up" />
            ) : (
              <i className="fa-solid fa-sort-down" />
            )
          ) : (
            <i className="fa-solid fa-sort text-gray-500 dark:text-secondary-400" />
          )}
        </span>
      </div>
    </th>
  );

  return (
    <div className="space-y-4">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {analysis.totalAmount != null && (
          <MetricCard
            label="Order Total"
            value={analysis.totalAmount.toLocaleString('en-US', { style: 'currency', currency: analysis.currency || 'USD' })}
            icon="fa-dollar-sign"
            color="green"
          />
        )}
        <MetricCard
          label="Line Items"
          value={(analysis.lineItemCount ?? 0).toString()}
          icon="fa-list"
          color="blue"
        />
        {analysis.vendorName && (
          <MetricCard
            label="Vendor"
            value={analysis.vendorName}
            icon="fa-store"
            color="purple"
          />
        )}
        {analysis.companyName && (
          <MetricCard
            label="Company"
            value={analysis.companyName}
            icon="fa-building"
            color="indigo"
          />
        )}
      </div>

      {/* Flags */}
      <div className="flex flex-wrap gap-2">
        {analysis.hasInternationalShipping && (
          <FlagBadge icon="fa-globe" color="blue">
            International Shipping ({analysis.detectedCountries?.join(', ') || 'Unknown'})
          </FlagBadge>
        )}
        {analysis.hasMultipleShipTos && (
          <FlagBadge icon="fa-location-dot" color="orange">
            Multiple Ship-To Addresses
          </FlagBadge>
        )}
        {analysis.hasHighValueItems && analysis.maxLineItemValue != null && (
          <FlagBadge icon="fa-gem" color="yellow">
            High-Value Items (max: {analysis.maxLineItemValue.toLocaleString('en-US', { style: 'currency', currency: analysis.currency || 'USD' })})
          </FlagBadge>
        )}
        {analysis.priority && (
          <FlagBadge icon="fa-flag" color="red">
            Priority: {analysis.priority}
          </FlagBadge>
        )}
      </div>

      {/* Discovered Fields */}
      {analysis.discoveredFields && analysis.discoveredFields.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700 dark:text-secondary-200">
              <i className="fa-solid fa-search mr-2" />
              Discovered Fields ({filteredAndSortedFields.length}
              {filterText && ` of ${analysis.discoveredFields.length}`})
            </h4>
            <div className="relative">
              <input
                type="text"
                placeholder="Filter fields..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="pl-8 pr-8 py-1 text-sm border border-gray-300 dark:border-secondary-600 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-48 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
              />
              <i className="fa-solid fa-filter absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-secondary-500 text-xs" />
              {filterText && (
                <button
                  onClick={() => setFilterText('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200"
                >
                  <i className="fa-solid fa-times text-xs" />
                </button>
              )}
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-secondary-900 rounded-lg max-h-80 overflow-y-auto overflow-x-auto">
            <table className="w-full text-sm table-fixed">
              <thead className="sticky top-0 bg-gray-50 dark:bg-secondary-900">
                <tr className="text-left text-gray-500 dark:text-secondary-400">
                  <th className="pb-2 pt-3 px-3 w-6"></th>
                  <SortableHeader field="fieldName" label="Field" className="w-28" />
                  <SortableHeader field="jsonPath" label="JSON Path" className="w-48" />
                  <SortableHeader field="currentValue" label="Value" className="w-32" />
                  <SortableHeader field="category" label="Category" className="w-24" />
                  <SortableHeader field="usefulnessScore" label="Score" className="w-14 pr-3" />
                </tr>
                <tr>
                  <th colSpan={6} className="h-px bg-gray-200 dark:bg-secondary-700"></th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedFields.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500 dark:text-secondary-400">
                      <i className="fa-solid fa-search text-2xl mb-2" />
                      <p>No fields match "{filterText}"</p>
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedFields.map((field, idx) => {
                    const isExpanded = expandedField === field.jsonPath;
                    return (
                      <React.Fragment key={idx}>
                        <tr 
                          className={cn(
                            'cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600 transition-colors',
                            isExpanded && 'bg-blue-50 dark:bg-blue-950'
                          )}
                          onClick={() => setExpandedField(isExpanded ? null : field.jsonPath)}
                        >
                          <td className="py-2 px-3 text-gray-400 dark:text-secondary-500">
                            <i className={cn(
                              'fa-solid fa-chevron-right text-xs transition-transform',
                              isExpanded && 'rotate-90'
                            )} />
                          </td>
                          <td className="py-2 font-medium text-gray-900 dark:text-white truncate" title={field.fieldName}>
                            {field.fieldName}
                            {field.filterBasedJsonPath && (
                              <i 
                                className="fa-solid fa-filter text-xs text-green-600 dark:text-green-400 ml-1" 
                                title="Filter-based path available (position-independent)"
                              />
                            )}
                          </td>
                          <td className="py-2">
                            <code 
                              className="font-mono text-xs text-gray-600 dark:text-secondary-400 block truncate" 
                              title={field.filterBasedJsonPath || field.jsonPath}
                            >
                              {field.filterBasedJsonPath || field.jsonPath}
                            </code>
                          </td>
                          <td className="py-2 text-gray-700 dark:text-secondary-200 truncate" title={field.currentValue || ''}>
                            {field.currentValue || '-'}
                          </td>
                          <td className="py-2">
                            <CategoryBadge category={field.category} />
                          </td>
                          <td className="py-2 pr-3 text-right">
                            <ScoreBadge score={field.usefulnessScore} />
                          </td>
                        </tr>
                        {isExpanded && field.parentObject ? (
                          <tr key={`detail-${idx}`}>
                            <td colSpan={6} className="px-3 pb-3">
                              <div className="bg-gray-900 rounded-lg p-3 mt-1">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs text-gray-400 dark:text-secondary-500 font-medium">
                                    Parent Object Context
                                  </span>
                                  <div className="flex items-center gap-2">
                                    {onAddRoutingRule && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onAddRoutingRule({
                                            field: field.filterBasedJsonPath || field.jsonPath,
                                            operator: 'equals',
                                            value: String(field.currentValue ?? ''),
                                          });
                                        }}
                                        className="text-xs bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded transition-colors"
                                      >
                                        <i className="fa-solid fa-route mr-1" />
                                        Use as Routing Rule
                                      </button>
                                    )}
                                    {onCreateOrderAttribute && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onCreateOrderAttribute(field);
                                        }}
                                        className="text-xs bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded transition-colors"
                                      >
                                        <i className="fa-solid fa-pencil mr-1" />
                                        Create Order Attribute
                                      </button>
                                    )}
                                    {onAddRoutingRuleToWorkflow && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onAddRoutingRuleToWorkflow(field);
                                        }}
                                        className="text-xs bg-violet-600 hover:bg-violet-700 text-white px-2 py-1 rounded transition-colors"
                                      >
                                        <i className="fa-solid fa-route mr-1" />
                                        Add to Workflow Rules
                                      </button>
                                    )}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigator.clipboard.writeText(
                                          JSON.stringify(field.parentObject, null, 2)
                                        );
                                      }}
                                      className="text-xs text-gray-400 dark:text-secondary-500 hover:text-white transition-colors"
                                    >
                                      <i className="fa-solid fa-copy mr-1" />
                                      Copy
                                    </button>
                                  </div>
                                </div>
                                <pre className="text-xs text-gray-100 overflow-x-auto max-h-48 overflow-y-auto">
                                  <JsonWithHighlight 
                                    json={field.parentObject} 
                                    highlightKey={field.fieldName} 
                                  />
                                </pre>
                              </div>
                            </td>
                          </tr>
                        ) : isExpanded ? (
                          <tr key={`detail-${idx}`}>
                            <td colSpan={6} className="px-3 pb-3">
                              <div className="bg-gray-100 dark:bg-secondary-700 rounded-lg p-3 mt-1 flex items-center justify-between">
                                <span className="text-sm text-gray-500 dark:text-secondary-400">
                                  <i className="fa-solid fa-info-circle mr-2" />
                                  Parent object context not available
                                </span>
                                <div className="flex items-center gap-2">
                                  {onAddRoutingRule && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onAddRoutingRule({
                                          field: field.filterBasedJsonPath || field.jsonPath,
                                          operator: 'equals',
                                          value: String(field.currentValue ?? ''),
                                        });
                                      }}
                                      className="text-xs bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded transition-colors"
                                    >
                                      <i className="fa-solid fa-route mr-1" />
                                      Use as Routing Rule
                                    </button>
                                  )}
                                  {onCreateOrderAttribute && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onCreateOrderAttribute(field);
                                      }}
                                      className="text-xs bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded transition-colors"
                                    >
                                      <i className="fa-solid fa-pencil mr-1" />
                                      Create Order Attribute
                                    </button>
                                  )}
                                  {onAddRoutingRuleToWorkflow && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onAddRoutingRuleToWorkflow(field);
                                      }}
                                      className="text-xs bg-violet-600 hover:bg-violet-700 text-white px-2 py-1 rounded transition-colors"
                                    >
                                      <i className="fa-solid fa-route mr-1" />
                                      Add to Workflow Rules
                                    </button>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}

/** Renders JSON with a specific key highlighted */
function JsonWithHighlight({ json, highlightKey }: { json: unknown; highlightKey: string }) {
  const jsonString = JSON.stringify(json, null, 2);
  
  // Find and highlight the key
  const keyPattern = new RegExp(`("${highlightKey}"\\s*:)`, 'g');
  const parts = jsonString.split(keyPattern);
  
  return (
    <>
      {parts.map((part, idx) => {
        if (keyPattern.test(`"${highlightKey}":`)) {
          keyPattern.lastIndex = 0; // Reset regex state
        }
        const isHighlighted = part.startsWith(`"${highlightKey}"`);
        return (
          <span 
            key={idx} 
            className={isHighlighted ? 'bg-yellow-500/30 text-yellow-300 rounded px-0.5' : ''}
          >
            {part}
          </span>
        );
      })}
    </>
  );
}

function MatchingRecommendationsSection({
  recommendations,
  onApply,
}: {
  recommendations: MatchingRecommendation[];
  onApply?: (rec: MatchingRecommendation) => void;
}) {
  if (recommendations.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-secondary-400">
        <i className="fa-solid fa-filter text-4xl mb-3" />
        <p>No matching criteria recommendations available.</p>
        <p className="text-sm">Add more data to the order to get recommendations.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {recommendations.map((rec, idx) => (
        <div
          key={idx}
          className="border border-gray-200 dark:border-secondary-700 rounded-lg p-4 hover:border-blue-300 transition-colors"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <CategoryBadge category={rec.category} />
                <ConfidenceBadge confidence={rec.confidence} />
                <span className="text-sm text-gray-500 dark:text-secondary-400">Priority #{rec.priority}</span>
              </div>
              <code className="font-mono text-sm text-gray-900 dark:text-white bg-gray-100 dark:bg-secondary-700 px-2 py-0.5 rounded">{rec.jsonPath}</code>
              <p className="text-sm text-gray-600 dark:text-secondary-400 mt-2">{rec.reason}</p>
              
              <div className="mt-3 bg-gray-50 dark:bg-secondary-900 rounded p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500 dark:text-secondary-400 w-24">Field Name:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{rec.fieldName}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500 dark:text-secondary-400 w-24">Current Value:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{rec.currentValue || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500 dark:text-secondary-400 w-24">Match Type:</span>
                  <span className="capitalize">{rec.recommendedMatchType}</span>
                </div>
              </div>

              <div className="mt-3 border-t border-gray-200 dark:border-secondary-700 pt-3">
                <h5 className="text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">Suggested Identifier:</h5>
                <div className="bg-blue-50 dark:bg-blue-950 rounded p-3 text-sm">
                  <div className="font-medium text-blue-900">{rec.suggestedIdentifier.name}</div>
                  {rec.suggestedIdentifier.description && (
                    <div className="text-blue-700 mt-1">{rec.suggestedIdentifier.description}</div>
                  )}
                  <div className="mt-2 font-mono text-xs text-blue-600 dark:text-blue-400">
                    {rec.suggestedIdentifier.jsonPath} {rec.suggestedIdentifier.operator}{' '}
                    {rec.suggestedIdentifier.regexPattern || rec.suggestedIdentifier.value}
                  </div>
                </div>
              </div>
            </div>

            {onApply && (
              <button
                onClick={() => onApply(rec)}
                className="ml-4 px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
              >
                <i className="fa-solid fa-plus mr-1" />
                Apply
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function ApprovalRecommendationsSection({
  recommendations,
  onApply,
}: {
  recommendations: { suggestedLevelCount: number; rationale: string; levels: WorkflowStepRecommendation[]; suggestedThresholdTiers: ThresholdTier[] };
  onApply?: (level: WorkflowStepRecommendation) => void;
}) {
  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <i className="fa-solid fa-list-check text-blue-600 dark:text-blue-400" />
          <span className="font-medium text-blue-900">
            Suggested: {recommendations.suggestedLevelCount} Step{recommendations.suggestedLevelCount !== 1 ? 's' : ''}
          </span>
        </div>
        <p className="text-sm text-blue-800">{recommendations.rationale}</p>
      </div>

      {/* Steps */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-secondary-200 mb-3">Recommended Levels</h4>
        <div className="space-y-3">
          {recommendations.levels.map((level) => (
            <div
              key={level.level}
              className="border border-gray-200 dark:border-secondary-700 rounded-lg p-4 hover:border-green-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-400 flex items-center justify-center font-bold">
                      {level.level}
                    </span>
                    <div>
                      <h5 className="font-medium text-gray-900 dark:text-white">{level.suggestedName}</h5>
                      {level.suggestedDescription && (
                        <p className="text-sm text-gray-600 dark:text-secondary-400">{level.suggestedDescription}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-secondary-400">Approver Type:</span>
                      <span className="ml-2 font-medium capitalize">{level.suggestedApproverType}</span>
                    </div>
                    {level.suggestedRole && (
                      <div>
                        <span className="text-gray-500 dark:text-secondary-400">Suggested Role:</span>
                        <span className="ml-2 font-medium">{level.suggestedRole}</span>
                      </div>
                    )}
                    {level.suggestedAmountThreshold && (
                      <div>
                        <span className="text-gray-500 dark:text-secondary-400">Threshold:</span>
                        <span className="ml-2 font-medium">
                          ${level.suggestedAmountThreshold.toLocaleString()}
                        </span>
                      </div>
                    )}
                    {level.suggestedTimeoutHours && (
                      <div>
                        <span className="text-gray-500 dark:text-secondary-400">Timeout:</span>
                        <span className="ml-2 font-medium">{level.suggestedTimeoutHours} hours</span>
                      </div>
                    )}
                  </div>

                  {level.suggestedSkipCondition && (
                    <div className="mt-3 bg-yellow-50 dark:bg-yellow-950 rounded p-2 text-sm">
                      <span className="text-yellow-700 dark:text-yellow-400">Skip when:</span>
                      <code className="ml-2 font-mono text-yellow-800">{level.suggestedSkipCondition}</code>
                    </div>
                  )}

                  <p className="text-sm text-gray-500 dark:text-secondary-400 mt-2 italic">{level.reason}</p>
                </div>

                {onApply && (
                  <button
                    onClick={() => onApply(level)}
                    className="ml-4 px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                  >
                    <i className="fa-solid fa-plus mr-1" />
                    Apply
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Threshold Tiers */}
      {recommendations.suggestedThresholdTiers && recommendations.suggestedThresholdTiers.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-secondary-200 mb-3">Suggested Threshold Tiers</h4>
          <div className="bg-gray-50 dark:bg-secondary-900 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 dark:bg-secondary-700">
                <tr>
                  <th className="px-4 py-2 text-left text-gray-600 dark:text-secondary-400">Tier</th>
                  <th className="px-4 py-2 text-left text-gray-600 dark:text-secondary-400">Amount Range</th>
                  <th className="px-4 py-2 text-center text-gray-600 dark:text-secondary-400">Levels</th>
                  <th className="px-4 py-2 text-left text-gray-600 dark:text-secondary-400">Suggested Roles</th>
                </tr>
              </thead>
              <tbody>
                {recommendations.suggestedThresholdTiers.map((tier, idx) => (
                  <tr key={idx} className="border-t border-gray-200 dark:border-secondary-700">
                    <td className="px-4 py-3 font-medium">{tier.label}</td>
                    <td className="px-4 py-3">
                      ${tier.minAmount.toLocaleString()}
                      {tier.maxAmount ? ` - $${tier.maxAmount.toLocaleString()}` : '+'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={cn(
                        'px-2 py-0.5 rounded-full text-xs font-medium',
                        tier.suggestedLevels === 0 ? 'bg-gray-100 dark:bg-secondary-700 text-gray-600 dark:text-secondary-400' :
                        tier.suggestedLevels === 1 ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-400' :
                        tier.suggestedLevels === 2 ? 'bg-blue-100 dark:bg-blue-900 text-blue-700' :
                        tier.suggestedLevels === 3 ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-400' :
                        'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-400'
                      )}>
                        {tier.suggestedLevels === 0 ? 'Auto-approve' : `${tier.suggestedLevels} level${tier.suggestedLevels !== 1 ? 's' : ''}`}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {tier.suggestedRoles.length > 0 ? tier.suggestedRoles.join(' → ') : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SkipConditionsSection({
  conditions,
  onApply,
}: {
  conditions: SkipConditionRecommendation[];
  onApply?: (condition: SkipConditionRecommendation) => void;
}) {
  if (conditions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-secondary-400">
        <i className="fa-solid fa-forward text-4xl mb-3" />
        <p>No skip condition recommendations available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {conditions.map((condition, idx) => (
        <div
          key={idx}
          className="border border-gray-200 dark:border-secondary-700 rounded-lg p-4 hover:border-purple-300 transition-colors"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <RiskBadge risk={condition.riskLevel} />
                <span className="text-sm text-gray-500 dark:text-secondary-400">
                  Applies to: {condition.appliesTo === 'workflow' ? 'Entire Workflow' : `Level ${condition.levelNumber}`}
                </span>
              </div>
              
              <h4 className="font-medium text-gray-900 dark:text-white">{condition.condition}</h4>
              
              <div className="mt-2 bg-gray-50 dark:bg-secondary-900 rounded p-2">
                <code className="font-mono text-sm text-gray-700 dark:text-secondary-200">{condition.expression}</code>
              </div>
              
              <p className="text-sm text-gray-600 dark:text-secondary-400 mt-2">{condition.reason}</p>
              
              {condition.estimatedImpact && (
                <p className="text-sm text-purple-600 dark:text-purple-400 mt-2">
                  <i className="fa-solid fa-chart-line mr-1" />
                  {condition.estimatedImpact}
                </p>
              )}
            </div>

            {onApply && (
              <button
                onClick={() => onApply(condition)}
                className="ml-4 px-3 py-1.5 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 transition-colors"
              >
                <i className="fa-solid fa-plus mr-1" />
                Apply
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// Helper components
function MetricCard({ label, value, icon, color }: { label: string; value: string; icon: string; color: string }) {
  const colorClasses: Record<string, string> = {
    green: 'bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400',
    blue: 'bg-blue-50 dark:bg-blue-950 text-blue-700',
    purple: 'bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-400',
    indigo: 'bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-400',
  };

  return (
    <div className={cn('rounded-lg p-3', colorClasses[color])}>
      <div className="flex items-center gap-2 mb-1">
        <i className={cn('fa-solid', icon, 'text-sm')} />
        <span className="text-xs font-medium opacity-80">{label}</span>
      </div>
      <div className="font-semibold truncate" title={value}>{value}</div>
    </div>
  );
}

function FlagBadge({ icon, color, children }: { icon: string; color: string; children: React.ReactNode }) {
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-100 dark:bg-blue-900 text-blue-700',
    orange: 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-400',
    yellow: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-400',
    red: 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-400',
  };

  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-1 rounded text-sm', colorClasses[color])}>
      <i className={cn('fa-solid', icon)} />
      {children}
    </span>
  );
}

function CategoryBadge({ category }: { category: string }) {
  const colors: Record<string, string> = {
    customer: 'bg-blue-100 dark:bg-blue-900 text-blue-700',
    vendor: 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-400',
    amount: 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-400',
    geography: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-400',
    department: 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-400',
    system: 'bg-gray-100 dark:bg-secondary-700 text-gray-700 dark:text-secondary-200',
    priority: 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-400',
    buyer: 'bg-pink-100 text-pink-700',
    other: 'bg-gray-100 dark:bg-secondary-700 text-gray-600 dark:text-secondary-400',
  };

  return (
    <span className={cn('px-2 py-0.5 rounded text-xs font-medium capitalize', colors[category] || colors.other)}>
      {category}
    </span>
  );
}

function ConfidenceBadge({ confidence }: { confidence: 'high' | 'medium' | 'low' }) {
  const colors = {
    high: 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-400',
    medium: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-400',
    low: 'bg-gray-100 dark:bg-secondary-700 text-gray-600 dark:text-secondary-400',
  };

  const icons = {
    high: 'fa-check-double',
    medium: 'fa-check',
    low: 'fa-question',
  };

  return (
    <span className={cn('px-2 py-0.5 rounded text-xs font-medium capitalize inline-flex items-center gap-1', colors[confidence])}>
      <i className={cn('fa-solid', icons[confidence], 'text-[10px]')} />
      {confidence}
    </span>
  );
}

function RiskBadge({ risk }: { risk: 'low' | 'medium' | 'high' }) {
  const colors = {
    low: 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-400',
    medium: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-400',
    high: 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-400',
  };

  return (
    <span className={cn('px-2 py-0.5 rounded text-xs font-medium', colors[risk])}>
      Risk: {risk}
    </span>
  );
}

function ScoreBadge({ score }: { score: number }) {
  const color = score >= 80 ? 'text-green-600 dark:text-green-400' : score >= 60 ? 'text-yellow-600' : 'text-gray-500 dark:text-secondary-400';
  return <span className={cn('font-medium', color)}>{score}</span>;
}

export default RecommendationsPanel;
